import { SectionHeader, SectionWrap } from "@/components/Section";
import { Check } from "lucide-react";
import { LQIP } from "@/constants/lqip";

const PILLARS = [
  "Biocompatible flexible interface design",
  "Scalable neural signal collection",
  "Real-time activity mapping",
  "Physics-aware AI modeling",
  "Personalized biofeedback loops",
];

export function TechnologySection() {
  return (
    <SectionWrap id="technology">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
        {/* LEFT — headline + intro + checkmark pillars */}
        <div className="lg:col-span-6">
          <SectionHeader
            num="03"
            kicker="Technology"
            title="Silk-inspired flexibility. AI-guided precision."
            sub="Our technology vision centers on soft, biocompatible interface concepts designed to improve compatibility and long-term signal stability compared with rigid traditional probes."
          />
          <p className="reveal mt-8 text-[#94A3B8] text-[15px] leading-relaxed max-w-xl">
            The platform is designed to help translate raw neural activity into
            interpretable data that can support personalized feedback and
            future neuroplasticity applications.
          </p>

          <div className="reveal mt-10 space-y-3">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-4 group"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <span className="h-7 w-7 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8]/30 text-[#38BDF8] grid place-items-center shrink-0">
                  <Check size={14} strokeWidth={2.5} />
                </span>
                <span className="text-[#CBD5E1] text-[15px]">{p}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — upgraded Why Neurobolt artwork */}
        <div className="lg:col-span-6">
          <figure
            className="reveal relative rounded-3xl overflow-hidden border border-white/10 bg-[#040A18]"
            style={{
              backgroundImage: `url(${LQIP.why_neurobolt})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <picture>
              <source srcSet="/assets/generated/why_neurobolt.avif" type="image/avif" />
              <source srcSet="/assets/generated/why_neurobolt.webp" type="image/webp" />
              <img
                src="/assets/generated/why_neurobolt.png"
                alt="Why Neurobolt — three principles: Biocompatible (long-term signal compatibility), Personalized (adaptive feedback around individual circuit dynamics), Scalable (continuous monitoring, modeling, and data growth)."
                loading="lazy"
                decoding="async"
                className="w-full h-auto"
              />
            </picture>
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(700px 220px at 50% 100%, rgba(56,189,248,0.16), transparent 70%)",
              }}
            />
          </figure>

          <div className="reveal mt-6 grid grid-cols-3 gap-3">
            <Stat k="Biocompatible" v="Long-term signal compatibility" c="#38BDF8" />
            <Stat k="Personalized" v="Individual circuit feedback" c="#6366F1" />
            <Stat k="Scalable" v="Continuous monitoring vision" c="#8B5CF6" />
          </div>
        </div>
      </div>
    </SectionWrap>
  );
}

function Stat({ k, v, c }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8]">
          {k}
        </span>
      </div>
      <p className="mt-2 text-[12.5px] text-[#CBD5E1] leading-snug">{v}</p>
    </div>
  );
}
